<template>
  <div class="organic-media-grid">
    <div
      v-for="(item, index) in mediaItemsWithLayout"
      :key="item.key || index"
      class="organic-media-item"
      :class="[`span-${item.colSpan}`, item.sizeClass]"
      :style="{ '--random-offset': item.randomOffset }"
      @click="$emit('media-click', { item: item.media, index })"
    >
      <!-- Image -->
      <img
        v-if="
          item.media.type === 'image' ||
          (!item.media.type && isImageUrl(item.media.url))
        "
        :src="item.media.url"
        :alt="item.media.caption || `Media ${index + 1}`"
        class="organic-media-element"
        loading="lazy"
        decoding="async"
      />
      <!-- Video -->
      <div
        v-else-if="item.media.type === 'video'"
        class="organic-video-wrapper"
      >
        <video
          :src="item.media.url"
          :poster="item.media.poster"
          class="organic-media-element organic-video"
          preload="metadata"
          playsinline
          muted
        ></video>
        <div class="organic-play-overlay">
          <svg class="organic-play-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7z" fill="currentColor" />
          </svg>
        </div>
      </div>
      <!-- Audio -->
      <div
        v-else-if="item.media.type === 'audio'"
        class="organic-audio-wrapper"
      >
        <div class="organic-audio-cover" v-if="item.media.cover">
          <img
            :src="item.media.cover"
            alt="Audio cover"
            class="organic-media-element"
          />
        </div>
        <div class="organic-audio-placeholder" v-else>
          <svg
            class="organic-audio-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div class="organic-audio-badge">
          <svg class="organic-audio-badge-icon" viewBox="0 0 24 24">
            <use href="@/assets/icons/iconset.svg#audio_compact"></use>
          </svg>
        </div>
      </div>
      <!-- Document -->
      <div
        v-else-if="item.media.type === 'document'"
        class="organic-document-wrapper"
      >
        <div class="organic-document-card">
          <svg
            class="organic-document-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <use href="@/assets/icons/iconset.svg#file"></use>
          </svg>
          <span class="organic-document-name">{{
            getDocumentName(item.media.url)
          }}</span>
        </div>
      </div>
      <!-- Caption under media (bottom-left aligned) -->
      <div v-if="item.media.caption" class="organic-media-caption">
        <span>{{ item.media.caption }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  name: "OrganicMediaGrid",
  props: {
    /**
     * Array of media items: { url, type?, caption?, cover?, poster? }
     * type: 'image' | 'video' | 'audio' | 'document'
     */
    mediaItems: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * Seed for deterministic random layout (e.g., item.id)
     * Ensures same item always gets same layout
     */
    seed: {
      type: String,
      default: "",
    },
  },
  emits: ["media-click"],
  setup(props) {
    // Simple seeded random number generator for deterministic layouts
    const seededRandom = (seed, index) => {
      const str = `${seed}-${index}`;
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      // Normalize to 0-1
      return Math.abs(hash % 1000) / 1000;
    };

    // Determine column span and size variation for each item
    const mediaItemsWithLayout = computed(() => {
      const items = props.mediaItems || [];
      const result = [];
      let usedCols = 0;
      const maxCols = 4;

      items.forEach((media, index) => {
        const rand = seededRandom(props.seed, index);

        // Determine column span: 1, 2, or occasionally 3 for variety
        // More likely to be 1 or 2, rarely 3 (for featured items)
        let colSpan;
        const remainingCols = maxCols - (usedCols % maxCols);

        if (items.length === 1) {
          // Single item spans full width
          colSpan = 4;
        } else if (items.length === 2) {
          // Two items: each spans 2 columns
          colSpan = 2;
        } else if (rand < 0.15 && remainingCols >= 3) {
          // 15% chance of large (3-col) if space allows
          colSpan = 3;
        } else if (rand < 0.5 && remainingCols >= 2) {
          // 35% chance of medium (2-col)
          colSpan = 2;
        } else {
          // Default: small (1-col)
          colSpan = 1;
        }

        // Ensure we don't exceed remaining space in current row
        if (colSpan > remainingCols && remainingCols > 0) {
          colSpan = remainingCols;
        }

        // Size class for subtle height variations
        const sizeRand = seededRandom(props.seed, index + 100);
        let sizeClass = "size-normal";
        if (sizeRand < 0.2) {
          sizeClass = "size-tall";
        } else if (sizeRand > 0.8) {
          sizeClass = "size-short";
        }

        // Random offset for organic feel (small margin variations)
        const randomOffset = Math.round(
          seededRandom(props.seed, index + 200) * 8,
        );

        result.push({
          media,
          colSpan,
          sizeClass,
          randomOffset,
          key: media.url || `media-${index}`,
        });

        usedCols += colSpan;
      });

      return result;
    });

    const isImageUrl = (url) => {
      if (!url) return false;
      const ext = url.split("?")[0].split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp", "avif", "svg"].includes(ext);
    };

    const getDocumentName = (url) => {
      if (!url) return "Document";
      const parts = url.split("/");
      const filename = parts[parts.length - 1].split("?")[0];
      // Truncate if too long
      return filename.length > 20 ? filename.slice(0, 17) + "..." : filename;
    };

    return {
      mediaItemsWithLayout,
      isImageUrl,
      getDocumentName,
    };
  },
};
</script>

<style scoped>
.organic-media-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3, 6px);
  width: 100%;
}

.organic-media-item {
  position: relative;
  cursor: pointer;
  border-radius: var(--radius-sm, 8px);
  overflow: hidden;
  background: var(--color-surface-raised, #f5f5f5);
  /* Subtle margin variation for organic feel */
  margin-top: calc(var(--random-offset, 0) * 0.5px);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.organic-media-item:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

/* Column spans */
.organic-media-item.span-1 {
  grid-column: span 1;
}
.organic-media-item.span-2 {
  grid-column: span 2;
}
.organic-media-item.span-3 {
  grid-column: span 3;
}
.organic-media-item.span-4 {
  grid-column: span 4;
}

/* Size variations (height) */
.organic-media-item.size-normal .organic-media-element {
  aspect-ratio: auto;
}
.organic-media-item.size-tall .organic-media-element {
  min-height: 180px;
}
.organic-media-item.size-short .organic-media-element {
  max-height: 150px;
}

/* Media elements */
.organic-media-element {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

/* Video wrapper */
.organic-video-wrapper {
  position: relative;
}

.organic-video {
  width: 100%;
  height: auto;
  display: block;
}

.organic-play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.organic-play-icon {
  width: 24px;
  height: 24px;
  color: #fff;
  margin-left: 3px; /* Optical center for play icon */
}

/* Audio wrapper */
.organic-audio-wrapper {
  position: relative;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.organic-audio-placeholder {
  width: 100%;
  height: 120px;
  background: linear-gradient(
    135deg,
    var(--color-accent-surface, #e8d5ff) 0%,
    var(--color-accent-surface-subtle, #f3e8ff) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
}

.organic-audio-icon {
  width: 48px;
  height: 48px;
  color: var(--color-accent-primary, #9c27b0);
  opacity: 0.7;
}

.organic-audio-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 4px 6px;
}

.organic-audio-badge-icon {
  width: 16px;
  height: 16px;
  color: #fff;
}

/* Document wrapper */
.organic-document-wrapper {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4, 12px);
}

.organic-document-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2, 4px);
  text-align: center;
}

.organic-document-icon {
  width: 32px;
  height: 32px;
  color: var(--color-icon-subtle);
}

.organic-document-name {
  font-size: var(--font-size-label, 12px);
  color: var(--color-text-primary, #333);
  word-break: break-word;
}

/* Caption */
.organic-media-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--space-2, 4px) var(--space-3, 6px);
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: #fff;
  font-size: var(--font-size-label, 12px);
  line-height: 1.3;
  text-align: left;
}

/* Responsive: 2 columns on smaller screens */
@media (max-width: 590px) {
  .organic-media-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .organic-media-item.span-3,
  .organic-media-item.span-4 {
    grid-column: span 2;
  }
}
</style>
